/**
 * Cliente de la API.
 *
 * Dos responsabilidades que no conviene repartir por las pantallas:
 * llevar el token de acceso en cada petición, y traducir los errores
 * RFC 7807 de la API a un objeto con el que la interfaz pueda decidir.
 */

const BASE = '/api/v1';

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
    /** Lista de errores de campo cuando la validación falla. */
    readonly errores?: string[],
    readonly detalles?: Record<string, unknown>,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }

  get esSesionExpirada(): boolean {
    return this.estado === 401;
  }

  get esSinPermiso(): boolean {
    return this.estado === 403;
  }
}

let tokenAcceso: string | null = null;
/** Se invoca cuando la sesión ya no se puede renovar. */
let alExpirar: (() => void) | null = null;

export function fijarToken(token: string | null): void {
  tokenAcceso = token;
}

export function alExpirarSesion(callback: () => void): void {
  alExpirar = callback;
}

/**
 * Renovar la sesión sin que se note.
 *
 * El token de acceso dura quince minutos a propósito: si se filtra, sirve
 * poco tiempo. La continuidad la da la cookie de refresh, que vale una
 * jornada. Antes eso no se usaba más que al abrir la aplicación, así que
 * a los quince minutos la primera petición devolvía 401 y al usuario se
 * le mandaba a la pantalla de acceso a media captura, con la cookie
 * buena todavía en el navegador.
 *
 * Ahora un 401 no significa «fuera»: significa «pide otro token». Sólo
 * cuando el refresco también falla —la cookie caducó, la cuenta se
 * desactivó— se cierra la sesión de verdad.
 */
let refrescoEnCurso: Promise<boolean> | null = null;

/** Las rutas de acceso no se reintentan: su 401 es una respuesta, no un aviso. */
function esRutaDeAcceso(url: string): boolean {
  return url.includes(`${BASE}/auth/`);
}

async function renovarSesion(): Promise<boolean> {
  // Uno solo a la vez. Una pantalla que carga cinco tablas recibe cinco
  // 401 casi simultáneos; cinco refrescos en paralelo se estorban entre
  // sí y alguno acabaría con un token que ya no vale.
  refrescoEnCurso ??= fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then(async (respuesta) => {
      if (!respuesta.ok) return false;

      const cuerpo = (await respuesta.json()) as { accessToken?: string };
      if (!cuerpo.accessToken) return false;

      tokenAcceso = cuerpo.accessToken;
      return true;
    })
    .catch(() => false)
    .finally(() => { refrescoEnCurso = null; });

  return refrescoEnCurso;
}

/**
 * `fetch` con el token puesto y con la renovación resuelta.
 *
 * Lo usan también las subidas de archivo, que van con FormData y no
 * pueden pasar por `pedir`. Sin esto, adjuntar un PDF pasados los quince
 * minutos seguía sacando al usuario.
 */
export async function fetchAutenticado(
  url: string | URL,
  init: RequestInit = {},
): Promise<Response> {
  const conCredenciales = (): RequestInit => ({
    ...init,
    headers: {
      ...((init.headers as Record<string, string>) ?? {}),
      ...cabeceraAutorizacion(),
    },
    credentials: 'include',
  });

  let respuesta = await fetch(url, conCredenciales());

  if (respuesta.status === 401 && !esRutaDeAcceso(String(url))) {
    if (await renovarSesion()) respuesta = await fetch(url, conCredenciales());
    if (respuesta.status === 401) alExpirar?.();
  }

  return respuesta;
}

/**
 * Cabecera de autorización para las peticiones que no pasan por `pedir`.
 * La subida de evidencia usa FormData y va con fetch directo; sin esto
 * tendría que guardarse el token en otro sitio, que es justo lo que se
 * evita al tenerlo sólo en memoria.
 */
export function cabeceraAutorizacion(): Record<string, string> {
  return tokenAcceso ? { Authorization: `Bearer ${tokenAcceso}` } : {};
}

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  cuerpo?: unknown;
  parametros?: Record<string, string | number | undefined>;
}

/**
 * Los motivos de un error, vengan como lista o agrupados.
 *
 * La validación de campos manda un arreglo; otras comprobaciones mandan un
 * objeto con las razones dentro —la fortaleza de la contraseña llega como
 * `{ reglas: [...] }`—. Antes sólo se leía el arreglo, así que esas
 * razones viajaban en la respuesta y no las veía nadie: la pantalla decía
 * «no cumple los requisitos mínimos» sin decir cuáles, teniendo la lista
 * a mano.
 */
function listaDeErrores(errors: unknown): string[] | undefined {
  if (Array.isArray(errors)) return errors as string[];
  if (!errors || typeof errors !== 'object') return undefined;

  const juntos = Object.values(errors as Record<string, unknown>)
    .flatMap((v) => (Array.isArray(v) ? v : [v]))
    .filter((v): v is string => typeof v === 'string');

  return juntos.length > 0 ? juntos : undefined;
}

export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const url = new URL(`${BASE}${ruta}`, window.location.origin);

  for (const [clave, valor] of Object.entries(opciones.parametros ?? {})) {
    if (valor !== undefined && valor !== '') url.searchParams.set(clave, String(valor));
  }

  const respuesta = await fetchAutenticado(url, {
    method: opciones.metodo ?? 'GET',
    headers: opciones.cuerpo ? { 'Content-Type': 'application/json' } : {},
    body: opciones.cuerpo ? JSON.stringify(opciones.cuerpo) : undefined,
  });

  if (respuesta.status === 204) return undefined as T;

  const texto = await respuesta.text();
  const cuerpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      String(cuerpo.codigo_negocio ?? 'error'),
      String(cuerpo.detail ?? 'No se pudo completar la operación.'),
      listaDeErrores(cuerpo.errors),
      (cuerpo.errors as Record<string, unknown>) ?? undefined,
    );
  }

  return cuerpo as T;
}

// ═════════════════════════════════════════════════════════════ atajos

export const api = {
  get: <T>(ruta: string, parametros?: Opciones['parametros']) =>
    pedir<T>(ruta, { parametros }),

  post: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { metodo: 'POST', cuerpo }),

  /**
   * Sube un archivo.
   *
   * No pasa por `pedir` porque el cuerpo es un FormData: poner
   * `Content-Type: application/json` a un FormData rompe la frontera que
   * el navegador tiene que escribir solo. El resto —renovar la sesión y
   * traducir el error de la API— es el mismo, así que va aquí y no
   * copiado en cada pantalla que sube algo.
   */
  subir: async <T>(
    ruta: string,
    cuerpo: FormData,
    parametros?: Opciones['parametros'],
  ): Promise<T> => {
    const url = new URL(`${BASE}${ruta}`, window.location.origin);
    for (const [clave, valor] of Object.entries(parametros ?? {})) {
      if (valor !== undefined && valor !== '') url.searchParams.set(clave, String(valor));
    }

    const respuesta = await fetchAutenticado(url, { method: 'POST', body: cuerpo });

    const texto = await respuesta.text();
    const datos = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};

    if (!respuesta.ok) {
      throw new ErrorApi(
        respuesta.status,
        String(datos.codigo_negocio ?? 'error'),
        String(datos.detail ?? 'No se pudo leer el archivo.'),
        listaDeErrores(datos.errors),
        (datos.errors as Record<string, unknown>) ?? undefined,
      );
    }

    return datos as T;
  },

  patch: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { metodo: 'PATCH', cuerpo }),

  /** Reemplaza el recurso entero. Lo usa la matriz de permisos. */
  put: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { metodo: 'PUT', cuerpo }),

  borrar: <T>(ruta: string) => pedir<T>(ruta, { metodo: 'DELETE' }),

  /**
   * Descarga un archivo que la API genera.
   *
   * No sirve un enlace normal: el token vive sólo en memoria y no viaja
   * en una navegación. Se pide con fetch, se arma un blob y se dispara la
   * descarga con un enlace temporal.
   */
  descargar: async (
    ruta: string,
    parametros?: Opciones['parametros'],
    nombreSugerido?: string,
  ): Promise<void> => {
    const url = new URL(`${BASE}${ruta}`, window.location.origin);
    for (const [clave, valor] of Object.entries(parametros ?? {})) {
      if (valor !== undefined && valor !== '') url.searchParams.set(clave, String(valor));
    }

    const respuesta = await fetchAutenticado(url);

    if (!respuesta.ok) {
      const texto = await respuesta.text();
      const cuerpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
      throw new ErrorApi(
        respuesta.status,
        String(cuerpo.codigo_negocio ?? 'error'),
        String(cuerpo.detail ?? 'No se pudo generar el archivo.'),
      );
    }

    // El nombre lo manda la API en Content-Disposition; el sugerido es
    // sólo el respaldo por si un proxy se come la cabecera.
    const disposicion = respuesta.headers.get('Content-Disposition') ?? '';
    const encontrado = /filename="([^"]+)"/.exec(disposicion)?.[1];

    const blob = await respuesta.blob();
    const enlace = document.createElement('a');
    enlace.href = URL.createObjectURL(blob);
    enlace.download = encontrado ?? nombreSugerido ?? 'archivo';
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    URL.revokeObjectURL(enlace.href);
  },
};
